// neo_cogym — PDF 뷰어 (P1, PDF.js 래퍼)
// 페이지 넘김 + 페이지% 진도. R2 서명 URL을 클라이언트에서 렌더(서버 부하 0).
// pdfjs-dist 를 동적 import 하여 초기 번들에서 분리(코드 스플리팅).

import type { ProgressUpdate } from "./types";

export interface PdfViewerHandle {
  pageCount: number;
  goTo(page: number): Promise<void>;
  next(): Promise<void>;
  prev(): Promise<void>;
  current(): number;
  destroy(): void;
}

export interface PdfViewerOptions {
  url: string;
  canvas: HTMLCanvasElement;
  contentAssetId: string;
  startPage?: number;
  /** 페이지 이동 시 진도 보고 */
  onProgress?: (p: ProgressUpdate) => void;
}

export async function createPdfViewer(opts: PdfViewerOptions): Promise<PdfViewerHandle> {
  // 동적 import: pdfjs-dist (워커는 Vite의 URL 패턴으로 분리 로드)
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
  ).toString();

  const doc = await pdfjs.getDocument({ url: opts.url }).promise;
  const pageCount = doc.numPages;
  let page = Math.min(Math.max(1, opts.startPage ?? 1), pageCount);
  const ctx = opts.canvas.getContext("2d")!;
  let renderTask: { cancel(): void } | null = null;

  async function render(n: number) {
    renderTask?.cancel();
    const pdfPage = await doc.getPage(n);
    const viewport = pdfPage.getViewport({ scale: 1.5 });
    opts.canvas.width = viewport.width;
    opts.canvas.height = viewport.height;
    renderTask = pdfPage.render({ canvasContext: ctx, viewport });
    await (renderTask as { promise: Promise<void> } & { cancel(): void }).promise;
    report(n);
  }

  function report(n: number) {
    const pagePercent = Math.round((n / pageCount) * 10000) / 100;
    opts.onProgress?.({
      contentAssetId: opts.contentAssetId,
      lastPage: n,
      pagePercent,
      completed: n >= pageCount,
    });
  }

  await render(page);

  async function goTo(n: number) {
    page = Math.min(Math.max(1, n), pageCount);
    await render(page);
  }

  const handle: PdfViewerHandle = {
    pageCount,
    current: () => page,
    goTo,
    async next() {
      if (page < pageCount) await goTo(page + 1);
    },
    async prev() {
      if (page > 1) await goTo(page - 1);
    },
    destroy() {
      renderTask?.cancel();
      doc.destroy();
    },
  };
  return handle;
}
